/**
 * WP4d / #46610 — verification-prove contracts for Receipts, Packs, and
 * Audit-room (grants / redaction / expiry / access logs).
 *
 * Session-authenticated product UI API (`apiCall` / `useApi`). Distinct from
 * the token-scoped public catalog at `/api/verification/v1`.
 *
 * Environment: every call carries `env` (query) and/or
 * `x-excellent-verification-environment`. Object ids are namespaced
 * `demo:` / `real:` (WP2). Handlers must call `enforceVerificationEnvironment`
 * on surfaces `receipts` | `packs` | `downloads` | `audit-room`. Receipts and
 * packs include environment provenance. Cross-environment pack compose is
 * rejected server-side.
 *
 * HG11 (#46595): mutations use existing ROLE_CAPABILITIES — no new
 * capabilities. Share/revoke requires `audit-room:invite` (admin/owner;
 * owner escape is `verification:*`). Receipt replay requires `receipt:write`.
 * Pack compose/download/redact/expiry reuse `export:export`. Reads are
 * ungated at the Hono seam, same as WP4a overview.
 *
 * WP4a (overview/queue/findings/runs), WP4b (workflows/controls/policies),
 * and WP4c (evidence/connectors) are out of scope — do not edit those
 * contracts. Demo seed/reset is WP3. Schema is WP4g.
 */

import { z } from "zod";
import { assuranceProjectionSchema } from "@excellent/core/verification-protocol-client";
import type { Operation } from "../types";

export const VERIFICATION_PROVE_SCHEMA_VERSION = 1 as const;

const envName = z.enum(["demo", "real"]);
export const verificationProveEnvQuery = z.object({
  env: envName.optional(),
  actorId: z.string().min(1).max(200).optional(),
});
const verificationStatus = z.enum(["PASS", "MISMATCH", "FAIL", "UNKNOWN", "NOT_RUN", "PENDING"]);
const namespacedId = z.string().min(1).max(200);
const iso = z.string().min(1);
const packKind = z.enum(["audit_preparation", "grc_exchange", "offline_replay"]);
const signatureAlgorithm = z.enum(["none", "HMAC-SHA256", "Ed25519"]);
const accessAction = z.enum(["view", "download", "grant", "revoke", "redact", "expiry", "replay"]);

const provenance = z.object({
  environment: envName,
  demoScoped: z.boolean(),
  trustChain: envName,
});

export type VerificationProveEnvQuery = z.infer<typeof verificationProveEnvQuery>;
export type VerificationProveProvenance = z.infer<typeof provenance>;

export const verificationReceiptDto = z.object({
  id: namespacedId,
  runId: namespacedId,
  parentReceiptId: namespacedId.nullable(),
  status: verificationStatus,
  issuedAt: iso,
  engineVersion: z.string().min(1),
  payloadHash: z.string().min(1),
  signatureKeyId: z.string().min(1),
  signatureAlgorithm,
  checkCount: z.number().int().nonnegative(),
  evidenceHashes: z.array(z.string().min(1)),
  policyVersionRefs: z.array(namespacedId),
  /** @deprecated Compatibility metadata only. Never treat this as cryptographic proof. */
  replayVerified: z.boolean(),
  /** @deprecated Compatibility metadata only. */
  replayCheckedAt: iso.nullable(),
  supersedes: namespacedId.optional(),
  correctionReason: z.string().min(1).optional(),
  provenance,
});
export type VerificationReceiptDto = z.infer<typeof verificationReceiptDto>;

const packShare = z.object({
  actorId: namespacedId,
  grantedAt: iso,
  expiresAt: iso,
  views: z.number().int().nonnegative(),
  downloads: z.number().int().nonnegative(),
});

export const verificationPackDto = z.object({
  id: namespacedId,
  label: z.string().min(1),
  kind: packKind,
  createdAt: iso,
  createdByActorId: namespacedId,
  expiresAt: iso.nullable(),
  runIds: z.array(namespacedId),
  controlIds: z.array(namespacedId),
  receiptIds: z.array(namespacedId),
  artifactCount: z.number().int().nonnegative(),
  sizeBytes: z.number().int().nonnegative(),
  redactionCount: z.number().int().nonnegative(),
  bundleHash: z.string().min(1),
  /** @deprecated Compatibility metadata only. Real verification requires an exported envelope and trust material. */
  offlineVerified: z.boolean(),
  scopeLimitations: z.array(z.string().min(1)),
  sharedWith: z.array(packShare),
  downloadedAt: iso.nullable(),
  lastDownloadFilename: z.string().nullable(),
  lastDownloadBody: z.string().nullable(),
  provenance,
});
export type VerificationPackDto = z.infer<typeof verificationPackDto>;

export const verificationAuditGrantDto = z.object({
  id: namespacedId,
  packId: namespacedId,
  reviewerActorId: namespacedId,
  createdByActorId: namespacedId,
  grantedAt: iso,
  expiresAt: iso,
  revokedAt: iso.nullable(),
  views: z.number().int().nonnegative(),
  downloads: z.number().int().nonnegative(),
});
export type VerificationAuditGrantDto = z.infer<typeof verificationAuditGrantDto>;

export const verificationAccessLogDto = z.object({
  id: namespacedId,
  grantId: namespacedId.nullable(),
  packId: namespacedId.nullable(),
  receiptId: namespacedId.nullable(),
  actorId: namespacedId,
  action: accessAction,
  occurredAt: iso,
});
export type VerificationAccessLogDto = z.infer<typeof verificationAccessLogDto>;

const idParams = z.object({ id: namespacedId });
export type VerificationProveIdParams = z.infer<typeof idParams>;

const receiptsListQuery = verificationProveEnvQuery.extend({
  status: verificationStatus.optional(),
  runId: namespacedId.optional(),
});
export type VerificationReceiptsListQuery = z.infer<typeof receiptsListQuery>;

export const verificationReceiptsListResponse = z.object({
  schemaVersion: z.literal(VERIFICATION_PROVE_SCHEMA_VERSION),
  provenance,
  items: z.array(verificationReceiptDto),
});
export type VerificationReceiptsListResponse = z.infer<typeof verificationReceiptsListResponse>;

export const verificationReceiptGetResponse = z.object({
  schemaVersion: z.literal(VERIFICATION_PROVE_SCHEMA_VERSION),
  provenance,
  receipt: verificationReceiptDto,
  assurance: assuranceProjectionSchema,
});
export type VerificationReceiptGetResponse = z.infer<typeof verificationReceiptGetResponse>;

export const verificationReceiptReplayBody = z.object({
  actorId: namespacedId,
});
export type VerificationReceiptReplayBody = z.infer<typeof verificationReceiptReplayBody>;

export const verificationReceiptReplayResponse = z.object({
  schemaVersion: z.literal(VERIFICATION_PROVE_SCHEMA_VERSION),
  provenance,
  capability: z.string().min(1),
  receipt: verificationReceiptDto,
});
export type VerificationReceiptReplayResponse = z.infer<typeof verificationReceiptReplayResponse>;

const packsListQuery = verificationProveEnvQuery.extend({
  kind: packKind.optional(),
});
export type VerificationPacksListQuery = z.infer<typeof packsListQuery>;

export const verificationPacksListResponse = z.object({
  schemaVersion: z.literal(VERIFICATION_PROVE_SCHEMA_VERSION),
  provenance,
  items: z.array(verificationPackDto),
});
export type VerificationPacksListResponse = z.infer<typeof verificationPacksListResponse>;

export const verificationPackGetResponse = z.object({
  schemaVersion: z.literal(VERIFICATION_PROVE_SCHEMA_VERSION),
  provenance,
  pack: verificationPackDto,
});
export type VerificationPackGetResponse = z.infer<typeof verificationPackGetResponse>;

export const verificationPackComposeBody = z.object({
  actorId: namespacedId,
  label: z.string().min(1),
  runIds: z.array(namespacedId).min(1),
  receiptIds: z.array(namespacedId).optional(),
  controlIds: z.array(namespacedId).optional(),
  kind: packKind.optional(),
});
export type VerificationPackComposeBody = z.infer<typeof verificationPackComposeBody>;

export const verificationPackComposeResponse = z.object({
  schemaVersion: z.literal(VERIFICATION_PROVE_SCHEMA_VERSION),
  provenance,
  capability: z.string().min(1),
  pack: verificationPackDto,
});
export type VerificationPackComposeResponse = z.infer<typeof verificationPackComposeResponse>;

export const verificationPackDownloadBody = z.object({
  actorId: namespacedId,
});
export type VerificationPackDownloadBody = z.infer<typeof verificationPackDownloadBody>;

export const verificationPackDownloadResponse = z.object({
  schemaVersion: z.literal(VERIFICATION_PROVE_SCHEMA_VERSION),
  provenance,
  capability: z.string().min(1),
  pack: verificationPackDto,
  filename: z.string().min(1),
  body: z.string().min(1),
});
export type VerificationPackDownloadResponse = z.infer<typeof verificationPackDownloadResponse>;

export const verificationPackRedactBody = z.object({
  actorId: namespacedId,
  refs: z.array(z.string().min(1)).min(1),
});
export type VerificationPackRedactBody = z.infer<typeof verificationPackRedactBody>;

export const verificationPackRedactResponse = z.object({
  schemaVersion: z.literal(VERIFICATION_PROVE_SCHEMA_VERSION),
  provenance,
  capability: z.string().min(1),
  pack: verificationPackDto,
});
export type VerificationPackRedactResponse = z.infer<typeof verificationPackRedactResponse>;

export const verificationPackExpiryBody = z.object({
  actorId: namespacedId,
  expiresAt: iso.nullable(),
});
export type VerificationPackExpiryBody = z.infer<typeof verificationPackExpiryBody>;

export const verificationPackExpiryResponse = z.object({
  schemaVersion: z.literal(VERIFICATION_PROVE_SCHEMA_VERSION),
  provenance,
  capability: z.string().min(1),
  pack: verificationPackDto,
});
export type VerificationPackExpiryResponse = z.infer<typeof verificationPackExpiryResponse>;

export const verificationAuditRoomGetResponse = z.object({
  schemaVersion: z.literal(VERIFICATION_PROVE_SCHEMA_VERSION),
  provenance,
  grants: z.array(verificationAuditGrantDto),
  accessLogs: z.array(verificationAccessLogDto),
});
export type VerificationAuditRoomGetResponse = z.infer<typeof verificationAuditRoomGetResponse>;

export const verificationAuditGrantBody = z.object({
  actorId: namespacedId,
  packId: namespacedId,
  reviewerActorId: namespacedId,
  expiresAt: iso,
});
export type VerificationAuditGrantBody = z.infer<typeof verificationAuditGrantBody>;

export const verificationAuditGrantResponse = z.object({
  schemaVersion: z.literal(VERIFICATION_PROVE_SCHEMA_VERSION),
  provenance,
  capability: z.string().min(1),
  grant: verificationAuditGrantDto,
});
export type VerificationAuditGrantResponse = z.infer<typeof verificationAuditGrantResponse>;

export const verificationAuditRevokeBody = z.object({
  actorId: namespacedId,
});
export type VerificationAuditRevokeBody = z.infer<typeof verificationAuditRevokeBody>;

export const verificationAuditRevokeResponse = z.object({
  schemaVersion: z.literal(VERIFICATION_PROVE_SCHEMA_VERSION),
  provenance,
  capability: z.string().min(1),
  grant: verificationAuditGrantDto,
});
export type VerificationAuditRevokeResponse = z.infer<typeof verificationAuditRevokeResponse>;

const receiptsListOp: Operation<
  void,
  VerificationReceiptsListQuery,
  void,
  VerificationReceiptsListResponse
> = {
  method: "GET",
  path: () => "/api/verification/receipts",
  query: receiptsListQuery,
  response: verificationReceiptsListResponse,
};

const receiptsGetOp: Operation<
  VerificationProveIdParams,
  VerificationProveEnvQuery,
  void,
  VerificationReceiptGetResponse
> = {
  method: "GET",
  path: ({ id }) => `/api/verification/receipts/${encodeURIComponent(id)}`,
  params: idParams,
  query: verificationProveEnvQuery,
  response: verificationReceiptGetResponse,
};

const receiptsReplayOp: Operation<
  VerificationProveIdParams,
  VerificationProveEnvQuery,
  VerificationReceiptReplayBody,
  VerificationReceiptReplayResponse
> = {
  method: "POST",
  path: ({ id }) => `/api/verification/receipts/${encodeURIComponent(id)}/replay`,
  params: idParams,
  query: verificationProveEnvQuery,
  body: verificationReceiptReplayBody,
  response: verificationReceiptReplayResponse,
  invalidates: [receiptsListOp],
};

const packsListOp: Operation<
  void,
  VerificationPacksListQuery,
  void,
  VerificationPacksListResponse
> = {
  method: "GET",
  path: () => "/api/verification/packs",
  query: packsListQuery,
  response: verificationPacksListResponse,
};

const packsGetOp: Operation<
  VerificationProveIdParams,
  VerificationProveEnvQuery,
  void,
  VerificationPackGetResponse
> = {
  method: "GET",
  path: ({ id }) => `/api/verification/packs/${encodeURIComponent(id)}`,
  params: idParams,
  query: verificationProveEnvQuery,
  response: verificationPackGetResponse,
};

const packsComposeOp: Operation<
  void,
  VerificationProveEnvQuery,
  VerificationPackComposeBody,
  VerificationPackComposeResponse
> = {
  method: "POST",
  path: () => "/api/verification/packs",
  query: verificationProveEnvQuery,
  body: verificationPackComposeBody,
  response: verificationPackComposeResponse,
  invalidates: [packsListOp],
};

const packsDownloadOp: Operation<
  VerificationProveIdParams,
  VerificationProveEnvQuery,
  VerificationPackDownloadBody,
  VerificationPackDownloadResponse
> = {
  method: "POST",
  path: ({ id }) => `/api/verification/packs/${encodeURIComponent(id)}/download`,
  params: idParams,
  query: verificationProveEnvQuery,
  body: verificationPackDownloadBody,
  response: verificationPackDownloadResponse,
  invalidates: [packsListOp],
};

const packsRedactOp: Operation<
  VerificationProveIdParams,
  VerificationProveEnvQuery,
  VerificationPackRedactBody,
  VerificationPackRedactResponse
> = {
  method: "POST",
  path: ({ id }) => `/api/verification/packs/${encodeURIComponent(id)}/redact`,
  params: idParams,
  query: verificationProveEnvQuery,
  body: verificationPackRedactBody,
  response: verificationPackRedactResponse,
  invalidates: [packsListOp],
};

const packsExpiryOp: Operation<
  VerificationProveIdParams,
  VerificationProveEnvQuery,
  VerificationPackExpiryBody,
  VerificationPackExpiryResponse
> = {
  method: "POST",
  path: ({ id }) => `/api/verification/packs/${encodeURIComponent(id)}/expiry`,
  params: idParams,
  query: verificationProveEnvQuery,
  body: verificationPackExpiryBody,
  response: verificationPackExpiryResponse,
  invalidates: [packsListOp],
};

const auditRoomGetOp: Operation<
  void,
  VerificationProveEnvQuery,
  void,
  VerificationAuditRoomGetResponse
> = {
  method: "GET",
  path: () => "/api/verification/audit-room",
  query: verificationProveEnvQuery,
  response: verificationAuditRoomGetResponse,
};

const auditRoomGrantOp: Operation<
  void,
  VerificationProveEnvQuery,
  VerificationAuditGrantBody,
  VerificationAuditGrantResponse
> = {
  method: "POST",
  path: () => "/api/verification/audit-room/grants",
  query: verificationProveEnvQuery,
  body: verificationAuditGrantBody,
  response: verificationAuditGrantResponse,
  invalidates: [auditRoomGetOp, packsListOp],
};

const auditRoomRevokeOp: Operation<
  VerificationProveIdParams,
  VerificationProveEnvQuery,
  VerificationAuditRevokeBody,
  VerificationAuditRevokeResponse
> = {
  method: "POST",
  path: ({ id }) => `/api/verification/audit-room/grants/${encodeURIComponent(id)}/revoke`,
  params: idParams,
  query: verificationProveEnvQuery,
  body: verificationAuditRevokeBody,
  response: verificationAuditRevokeResponse,
  invalidates: [auditRoomGetOp, packsListOp],
};

export const verificationProve = {
  receiptsList: receiptsListOp,
  receiptsGet: receiptsGetOp,
  receiptsReplay: receiptsReplayOp,
  packsList: packsListOp,
  packsGet: packsGetOp,
  packsCompose: packsComposeOp,
  packsDownload: packsDownloadOp,
  packsRedact: packsRedactOp,
  packsExpiry: packsExpiryOp,
  auditRoomGet: auditRoomGetOp,
  auditRoomGrant: auditRoomGrantOp,
  auditRoomRevoke: auditRoomRevokeOp,
};
